// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import {
    ExportToProductListApiParameter,
    exportToProductListService,
} from "@werner/App/Services/ExportoProductListService";
import { getIsPreviouslyPurchasedPage } from "@werner/App/Store/Selectors/GeneralSelectors";
import { isPreviouslyPurchased } from "@werner/App/Store/Selectors/ProductSelectors";

type Parameter = ExportToProductListApiParameter & HasOnSuccess & HasOnError<unknown>;

type HandlerType = Handler<
    Parameter,
    {
        error?: unknown;
        apiParameter: ExportToProductListApiParameter;
    }
>;

export const PopUlateApiParameter: HandlerType = props => {
    const { onSuccess, onError, ...restParameter } = props.parameter;
    props.apiParameter = {
        ...restParameter,
        time: props.parameter.time || Date.now(),
    };
    if (restParameter.type !== "Cart" && restParameter.type !== "WishList") {
        const state = props.getState();
        const productFilters = state.pages.productList.productFilters;
        props.apiParameter = {
            ...props.apiParameter,
            searchWithin: productFilters.searchWithinQueries?.join(" "),
            previouslyPurchasedProducts:
                productFilters.previouslyPurchasedProducts || getIsPreviouslyPurchasedPage(state),
            attributeValueIds: productFilters.attributeValueIds,
            brandIds: productFilters.brandIds,
            stockedItemsOnly: productFilters.stockedItemsOnly,
        };
        if (restParameter.type === "SearchResults") {
            props.apiParameter.categoryId = productFilters.categoryId;
            props.apiParameter.query = productFilters.query;
        }
    }
};

export const RequestApi: HandlerType = async props => {
    try {
        await exportToProductListService(props.apiParameter);
    } catch (error) {
        props.error = error;
    }
};

export const DispatchOnSuccessExportCartToProductList: HandlerType = props => {
    if (!props.error) {
        props.parameter.onSuccess?.();
    }
};
export const DispatchOnErrorExportCartToProductList: HandlerType = props => {
    if (props.error) {
        props.parameter.onError?.(props.error);
    }
};

export const chain = [
    PopUlateApiParameter,
    RequestApi,
    DispatchOnSuccessExportCartToProductList,
    DispatchOnErrorExportCartToProductList,
];

const exportToProductList = createHandlerChainRunner(chain, "ExportToProductList");
export default exportToProductList;
